import Vue from "vue";
import Vuex from "vuex";
import { Colors, ConfiguratorConfig, Font } from "@/types/DataSource";
import { State } from "@/types/Store";

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    productFinderId: null,
    previousStates: [] as SavedState[],
    currentParent: null as Nullable<SavedState>,
    globalCss: {
      font: {
        fontName: `"Raleway", sans-serif`,
        fontSize: "16px",
        fontWeight: "regular"
      },
      colors: {
        contentWrapperBgColor: "white",
        buttonPrimary: {
          color: "white",
          backgroundColor: "black"
        },
        buttonSecondary: {
          color: "black",
          borderColor: "black"
        },
        buttonText: {
          color: "white",
          borderColor: "white"
        },
        buttonProduct: {
          color: "white",
          backgroundColor: "black"
        },
        modal: {
          color: "white",
          backgroundColor: "black"
        },
        description: {
          color: "black",
          backgroundColor: "white"
        },
        headline: {
          color: "black"
        },
        dropdownQuestion: {
          color: "black"
        },
        progressBar: {
          color: "black",
          backgroundColor: "white",
          labelColor: "black"
        },
        inputTextBox: {
          backgroundColorActive: "black",
          backgroundColorInactive: "white",
          colorActive: "white",
          colorInactive: "black"
        },
        inputImageTextBox: {
          backgroundColorActive: "black",
          backgroundColorInactive: "white",
          colorActive: "white",
          colorInactive: "black"
        },
        rating: {
          color: "black",
          iconSize: "9px"
        },
        topProductBadge: {
          color: "white",
          backgroundColor: "black"
        },
        errorMessage: {
          color: "black",
          backgroundColor: "white"
        }
      }
    },
    globalConfig: {
      onClickQuestions: false,
      ratingsEnabled: true,
      currency: "EUR",
      currencyFormat: "de-DE",
      productImageMode: "cover"
    }
  } as State,
  mutations: {
    /**
     * Sets custom user config
     * @param state Vuex store state
     * @param {ConfiguratorConfig} newConfig Custom productfinder config
     */
    setConfig(state, newConfig: ConfiguratorConfig) {
      if (newConfig.onClickQuestions != undefined)
        state.globalConfig.onClickQuestions = newConfig.onClickQuestions;

      if (newConfig.ratingsEnabled != undefined)
        state.globalConfig.ratingsEnabled = newConfig.ratingsEnabled;

      if (newConfig.currency != undefined)
        state.globalConfig.currency = newConfig.currency;

      if (newConfig.currencyFormat != undefined)
        state.globalConfig.currencyFormat = newConfig.currencyFormat;

      if (newConfig.productImageMode != undefined)
        state.globalConfig.productImageMode = newConfig.productImageMode;
    },
    /**
     * Sets custom user font
     * @param state Vuex store state
     * @param {Font} newFont Custom UI Font
     */
    setFont(state, newFont: Font) {
      if (newFont.fontName != undefined)
        state.globalCss.font.fontName = newFont.fontName;

      if (newFont.fontSize != undefined)
        state.globalCss.font.fontSize = newFont.fontSize;

      if (newFont.fontName != undefined)
        state.globalCss.font.fontSize = newFont.fontSize;
    },
    /**
     * Sets custom user colors
     * @param state Vuex store state
     * @param {Colors} newColors Custom UI colors
     */
    setColors(state, newColors: Colors) {
      state.globalCss.colors.contentWrapperBgColor =
        newColors.contentWrapperBgColor;

      state.globalCss.colors.buttonPrimary.color = newColors.buttonPrimaryColor;
      state.globalCss.colors.buttonPrimary.backgroundColor =
        newColors.buttonPrimaryBackgroundColor;

      state.globalCss.colors.buttonSecondary.color =
        newColors.buttonSecondaryColor;
      state.globalCss.colors.buttonSecondary.borderColor =
        newColors.buttonSecondaryBorderColor;

      state.globalCss.colors.buttonText.color = newColors.buttonTextColor;
      state.globalCss.colors.buttonText.borderColor =
        newColors.buttonTextColorBorderColor;

      state.globalCss.colors.buttonProduct.color = newColors.buttonProductColor;
      state.globalCss.colors.buttonProduct.backgroundColor =
        newColors.buttonProductBackgroundColor;

      state.globalCss.colors.modal.color = newColors.modalColor;
      state.globalCss.colors.modal.backgroundColor =
        newColors.modalBackgroundColor;

      state.globalCss.colors.description.color = newColors.descriptionColor;
      state.globalCss.colors.description.backgroundColor =
        newColors.descriptionBackgroundColor;

      state.globalCss.colors.headline.color = newColors.headlineColor;

      state.globalCss.colors.dropdownQuestion.color =
        newColors.dropdownQuestionColor;

      state.globalCss.colors.progressBar.color = newColors.progressBarColor;
      state.globalCss.colors.progressBar.backgroundColor =
        newColors.progressBarBackgroundColor;
      state.globalCss.colors.progressBar.labelColor =
        newColors.progressBarLabelColor;

      state.globalCss.colors.inputTextBox.backgroundColorActive =
        newColors.inputTextBoxBackgroundColorActive;
      state.globalCss.colors.inputTextBox.backgroundColorInactive =
        newColors.inputTextBoxBackgroundColorInactive;
      state.globalCss.colors.inputTextBox.colorActive =
        newColors.inputTextBoxColorActive;
      state.globalCss.colors.inputTextBox.colorInactive =
        newColors.inputTextBoxColorInactive;

      state.globalCss.colors.inputImageTextBox.backgroundColorActive =
        newColors.inputImageTextBoxBackgroundColorActive;
      state.globalCss.colors.inputImageTextBox.backgroundColorInactive =
        newColors.inputImageTextBoxBackgroundColorInactive;
      state.globalCss.colors.inputImageTextBox.colorActive =
        newColors.inputImageTextBoxColorActive;
      state.globalCss.colors.inputImageTextBox.colorInactive =
        newColors.inputImageTextBoxColorInactive;

      state.globalCss.colors.rating.color = newColors.ratingColor;
      state.globalCss.colors.rating.iconSize = newColors.ratingIconSize;

      state.globalCss.colors.topProductBadge.color =
        newColors.topProductBadgeColor;
      state.globalCss.colors.topProductBadge.backgroundColor =
        newColors.topProductBadgeBackgroundColor;

      state.globalCss.colors.errorMessage.color = newColors.errorMessageColor;
      state.globalCss.colors.errorMessage.backgroundColor =
        newColors.errorMessageBackgroundColor;
    },
    /**
     * Adds new state to previousStates
     * @param state Vuex store state
     * @param newState State to be added to previousStates
     */
    addNewState(state, newState: SavedState) {
      // mutate state
      state.previousStates.push(newState);
      // write new previousStates to local storage
      localStorage.setItem(
        state.productFinderId + "_previousStates",
        JSON.stringify(state.previousStates)
      );
    },
    /**
     * Resets all states after a certain state including the state
     * @param state Vuex store state
     * @param referenceState state that should be removed including everything after it
     */
    removeStatesAfter(state, referenceState: SavedState) {
      const indexOfReference = state.previousStates.indexOf(referenceState);
      state.previousStates.length = indexOfReference;

      // write new previousStates to local storage
      localStorage.setItem(
        state.productFinderId + "_previousStates",
        JSON.stringify(state.previousStates)
      );
    },
    /**
     * Resets all previousStates
     * @param state Vuex store state
     */
    resetStates(state) {
      state.previousStates = [];
      state.currentParent = null;
      //remove previousStates from local storage
      localStorage.removeItem(state.productFinderId + "_previousStates");
    },
    /**
     * Loads a passed set of previousStates into store
     * @param state Vuex store state
     * @param previousStates Previous states to be loaded into store
     */
    importPreviousStates(state, previousStates) {
      state.previousStates = previousStates;
    },
    /**
     * Loads a passed currentParent into store
     * @param state Vuex store state
     * @param currentParent current parent to be loaded into store
     */
    importCurrentParent(state, currentParent) {
      state.currentParent = currentParent;
    },
    /**
     * Sets currentParent
     * @param state Vuex store state
     * @param savedState SavedState currentParent should be set to
     */
    setCurrentParent(state, savedState) {
      state.currentParent = savedState;
      // write new currentParent to local storage
      localStorage.setItem(
        state.productFinderId + "_currentParent",
        JSON.stringify(state.currentParent)
      );
    },
    /**
     * Resets currentParent to null
     * @param state Vuex store state
     */
    resetCurrentParent(state) {
      state.currentParent = null;
      //remove currentParent from local storage
      localStorage.removeItem(state.productFinderId + "_currentParent");
    },
    setProductFinderId(state, productFinderId) {
      state.productFinderId = productFinderId;
    }
  },
  actions: {},
  modules: {}
});
